import * as React from "react";
import * as moment from "moment";
import {
    AppScope,
    ITransactionProductModel,
    ProductCodeProEnum,
    Transaction,
    TransactionType,
    UnhandledScopeError,
} from "@atman/business";
import { Badge } from "reactstrap";
import { BaseColumn } from "../../BaseItemList";
import { Colors } from "../../../styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { YouBadge } from "../..";

export class TransactionOtherPartyOwnerNameColumn extends BaseColumn<Transaction, string> {
    constructor(scope: AppScope) {
        super();

        switch (scope) {
            case AppScope.Partner:
                this.text = "partnerApp.clientName".localize();
                break;
            case AppScope.Supplier:
                this.text = "supplierApp.partnerName".localize();
                break;
            default:
                throw new UnhandledScopeError(scope);
        }

        this.dataField = "otherPartyOwnerName";
        this.formatter = (cell) => {
            if (!cell) {
                return <YouBadge />;
            }

            return <span>{cell}</span>;
        };
    }
}

export class TransactionTypeColumn extends BaseColumn<Transaction, TransactionType> {
    constructor(scope: AppScope) {
        super();

        this.dataField = "transactionType";
        this.text = "global.type".localize();
        this.sort = false;
        this.formatter = (cell, row) => {
            if (cell === undefined || cell === null) {
                return null;
            }

            let iconName: IconProp | null = null;
            let typeLabel: string = `global.${TransactionType[cell].toCamel()}`.localize();
            let color: string;

            switch (cell) {
                case TransactionType.Sale:
                    iconName = ["far", "dollar-sign"] as IconProp;
                    color = Colors.appGreenColor;

                    if (!row.otherPartyOwnerId && scope === AppScope.Partner) {
                        typeLabel = "global.purchase".localize();
                    }

                    break;
                case TransactionType.Promo:
                    iconName = ["far", "badge-check"] as IconProp;
                    color = Colors.appGreenColor;
                    break;
                case TransactionType.Refund:
                    iconName = ["far", "undo"] as IconProp;
                    color = Colors.appRedColor;
                    break;
                default:
                    color = Colors.veryDarkGrey;
            }

            return (
                <Badge
                    className="card-border textSizeMedium"
                    style={{ padding: "6px 12px", background: "#FAFAFA", color: Colors.veryDarkGrey, borderRadius: 24 }}
                >
                    {iconName ? <FontAwesomeIcon icon={iconName} color={color} style={{ marginRight: 8 }} /> : null}
                    <span className="type">{typeLabel}</span>
                </Badge>
            );
        };
    }
}

export class TransactionProductColumn extends BaseColumn<Transaction, ITransactionProductModel[]> {
    public key: string;

    constructor(formatExtraData?: unknown) {
        super(formatExtraData);

        this.dataField = "products";
        this.text = "global.product".localize();
        this.key = "productColumn";
        this.sort = false;
        this.formatter = (cell) => {
            if (!cell) {
                return <span></span>;
            }

            const productCode = cell[0].productCode;

            let cellContent = `${cell[0].quantity} ${`global.productCodes.${ProductCodeProEnum[
                productCode
            ].toCamel()}`.localize()}`;

            if (cell.length > 1) {
                cellContent += ` ${"global.andOthers".localize().replace("{0}", (cell.length - 1).toString())}`;
            }

            return <span className="textWeightMedium">{cellContent}</span>;
        };
    }
}

export class TransactionUserColumn extends BaseColumn<Transaction, string> {
    constructor(formatExtraData?: unknown) {
        super(formatExtraData);

        this.dataField = "userDisplayName";
        this.text = "global.itemTypes.user".localize();
        this.sort = false;
    }
}

export class TransactionDateColumn extends BaseColumn {
    constructor(formatExtraData?: unknown) {
        super(formatExtraData);

        this.dataField = "transactionDate";
        this.text = "global.date".localize();
        this.formatter = (cell: string) => {
            return <span className="textWeightRegular">{moment.parseZone(cell).format("LL")}</span>;
        };
    }
}
